import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { eChatEventTypes } from 'src/providers/SocketsProvider/Sockets.enums';
import { useChatContext } from 'src/components/Chat/Chat.hook';
import { UpcomingSendsService } from './UpcomingSendsBar.service';
import type { tPendingFollowUp } from './UpcomingSendsBar.types';

/**
 * A send produces several socket events in a row (the new message, the card
 * turning consumed); they collapse into one request.
 */
export const UPCOMING_SENDS_REFRESH_DEBOUNCE_MS = 400;

type tUpcomingSendsState = {
  chatId: string | undefined;
  items: tPendingFollowUp[];
};

export type tUseUpcomingSends = {
  /** The open chat's scheduled messages that have not gone out, soonest first. */
  pending: tPendingFollowUp[];
  /** Asks the server again, now. */
  refresh: () => void;
};

/**
 * INT-1500 U12 (D8): the open chat's pending scheduled messages, read from the
 * server independently of the thread's pagination, so the bar knows about a
 * card that sits in history the thread has not loaded.
 *
 * It asks again whenever something may have changed them: a new event in this
 * chat (the scheduled message itself going out arrives this way), an update to
 * one of its scheduled cards, a cancelled campaign behind one of them, and any
 * change to the follow-ups the chat keeps in memory (a card created, edited or
 * cancelled in this tab). A failed request reads as "nothing pending": the bar
 * hides rather than show a count it cannot back.
 *
 * Mount it only while the flag is on: it opens socket subscriptions and
 * requests of its own.
 */
export default function useUpcomingSends(): tUseUpcomingSends {
  const { selectedChat, followUps } = useChatContext();
  const { chatInsertedEvent, chatUpdatedEvent, campaignCancelledEvent } =
    useSocketsContext();
  const chatId = selectedChat?.id;

  const [state, setState] = useState<tUpcomingSendsState>({
    chatId: undefined,
    items: [],
  });
  const [refreshTick, setRefreshTick] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!chatId) return undefined;
    const controller = new AbortController();

    UpcomingSendsService.getPending(chatId, { signal: controller.signal })
      .then((items) => {
        if (!controller.signal.aborted) setState({ chatId, items });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ chatId, items: [] });
      });

    return () => controller.abort();
  }, [chatId, refreshTick]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const scheduleRefresh = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => setRefreshTick((tick) => tick + 1),
      UPCOMING_SENDS_REFRESH_DEBOUNCE_MS,
    );
  }, []);

  const refresh = useCallback(() => {
    clearTimeout(debounceRef.current);
    setRefreshTick((tick) => tick + 1);
  }, []);

  // Another chat's list never shows, not even for the render before its own
  // request answers.
  const pending = state.chatId === chatId ? state.items : [];

  chatInsertedEvent.useSubscription((event) => {
    if (chatId && event.chat_id === chatId) scheduleRefresh();
  });

  chatUpdatedEvent.useSubscription((event) => {
    if (
      chatId &&
      event.chat_id === chatId &&
      event.type === eChatEventTypes.FOLLOW_UP
    ) {
      scheduleRefresh();
    }
  });

  campaignCancelledEvent.useSubscription((event) => {
    const cancelled = String(event.campaign_id);
    if (pending.some((item) => String(item.campaignId) === cancelled)) {
      scheduleRefresh();
    }
  });

  // A card created, edited or cancelled in this tab changes the follow-ups the
  // chat keeps in memory. The value in memory when a chat opens is still the
  // previous chat's, and the request above already covers the new one, so it
  // is skipped; the chat's own follow-ups load, which lands right after, costs
  // one debounced request more.
  const followUpsSeenForChatRef = useRef<string | undefined>();
  useEffect(() => {
    if (!chatId) return;
    if (followUpsSeenForChatRef.current === chatId) scheduleRefresh();
    followUpsSeenForChatRef.current = chatId;
  }, [followUps, chatId, scheduleRefresh]);

  return { pending, refresh };
}
